/* eslint-disable sonarjs/cognitive-complexity, eslint-comments/no-restricted-disable -- TODO: Refactor this file */
import { paywallCheckoutApi } from "@circle-react/api/paywallCheckoutApi";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { useNavigate } from "@tanstack/react-router";
import { useRef } from "react";
import { useMutation } from "react-query";

import { stripeElementsModes } from "../../../StripeElements/types";
import { CheckoutError } from "../../CheckoutError";
import { useCheckoutSession } from "../../contexts/CheckoutSessionContext";
import { constructReturnUrl, constructReturnUrlPath } from "../../helpers";
import { generateIdempotencyKey } from "../../helpers/generateIdempotencyKey";
import { useGetStripeElements } from "../../useStripeElements";
import { usePaymentMethodDataParams } from "../paymentMethods/usePaymentMethodDataParams";

class PostAttemptCheckoutError extends Error {
  readonly checkoutError: CheckoutError;

  constructor(checkoutError: CheckoutError) {
    super(checkoutError.message, { cause: checkoutError });
    this.name = "PostAttemptCheckoutError";
    this.checkoutError = checkoutError;
  }
}

export const useCard = () => {
  const getStripeElements = useGetStripeElements();
  const idempotencyKeyRef = useRef<string | undefined>(undefined);

  const navigate = useNavigate();
  const {
    currentCommunity,
    getSelectedPrice,
    isCardInfoRequired,
    isMemberUsingSavedPaymentMethod,
    stripeElementsMode,
    stripePaymentMethodType,
  } = usePaywallCheckoutContext();
  const { ownershipToken, session } = useCheckoutSession();
  const checkoutIntentToken = session?.checkout_intent_token;
  const { paymentMethodDataParams } = usePaymentMethodDataParams();

  // If we are using PMC, we only require a to setup a payment method if we are in setup mode.
  const isSettingUpPaymentMethod =
    stripeElementsMode === stripeElementsModes.setup;

  const submitStripeElement = async (caller: string): Promise<boolean> => {
    const { stripeElements: elements } = getStripeElements(caller);

    const result = await elements.submit();
    const error = result?.error;
    if (error) {
      throw CheckoutError.fromStripeError(caller, error);
    }
    return true;
  };

  const selectedPrice = getSelectedPrice();

  const verifyCheckoutAttempt = ({
    paymentIntentId,
    invoiceId,
  }: {
    paymentIntentId?: string;
    invoiceId?: string;
  }) => {
    const verificationUrlPath = constructReturnUrlPath({
      paymentMethodType: stripePaymentMethodType,
      paymentIntentId,
      invoiceId,
      sessionId: session?.id,
    });
    void navigate({ to: verificationUrlPath, replace: true });
  };

  const processPaymentInStripe = async (checkoutResponseBody: {
    checkout_attempt_id: number;
    payment_intent_processor_id?: string;
    payment_intent_client_secret?: string;
    invoice_processor_id?: string;
  }) => {
    const { stripe, stripeElements: elements } = getStripeElements(
      "useCard.processPaymentInStripe",
    );

    const {
      payment_intent_processor_id: paymentIntentId,
      payment_intent_client_secret: paymentIntentClientSecret,
      invoice_processor_id: invoiceId,
    } = checkoutResponseBody;

    // No payment intent? We are looking at a trial or $0 invoice. Straight to verification.
    if (!paymentIntentId || !paymentIntentClientSecret) {
      verifyCheckoutAttempt({ invoiceId });
      return;
    }

    const paymentIntentResult = await stripe.retrievePaymentIntent(
      paymentIntentClientSecret,
    );
    if (paymentIntentResult.error) {
      throw CheckoutError.fromStripeError(
        "useCard.processPaymentInStripe.retrievePaymentIntent",
        paymentIntentResult.error,
      );
    }

    // Payment intent succeeded? Straight to verification.
    const paymentIntent = paymentIntentResult?.paymentIntent;
    if (paymentIntent?.status === "succeeded") {
      verifyCheckoutAttempt({ paymentIntentId });
      return;
    }

    // If we reach this point and we required a setup intent, we need to resubmit the Stripe element
    // before we can confirm the payment intent. That's because the first submission was for the setup
    // intent, not the payment intent.
    if (isSettingUpPaymentMethod) {
      await submitStripeElement(
        "useCard.processPaymentInStripe.isSettingUpPaymentMethod",
      );
    }

    const confirmPaymentResult = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntentClientSecret,
      confirmParams: {
        ...paymentMethodDataParams,
        return_url: constructReturnUrl({
          paymentMethodType: stripePaymentMethodType,
          invoiceId,
          sessionId: session?.id,
        }),
      },
      redirect: "if_required",
    });
    if (confirmPaymentResult.error) {
      throw CheckoutError.fromStripeError(
        "useCard.processPaymentInStripe.confirmPayment",
        confirmPaymentResult.error,
      );
    }

    verifyCheckoutAttempt({ paymentIntentId, invoiceId });
  };

  const execCheckoutRequest = async (formData: any) => {
    const checkoutResponse = await paywallCheckoutApi.create({
      formData: {
        ...formData,
        payment_method_type: stripePaymentMethodType,
        community_member_adding_payment_method:
          !isMemberUsingSavedPaymentMethod,
        idempotency_key: idempotencyKeyRef.current,
        checkout_intent_token: checkoutIntentToken,
      },
      ownershipToken,
      sessionId: session?.id,
    });
    if (!checkoutResponse || !checkoutResponse.ok) {
      const error = await checkoutResponse?.json?.();
      throw CheckoutError.fromPaywallError(
        "useCard.execCheckoutRequest.create",
        error,
      );
    }

    const checkoutResponseBody = await checkoutResponse.json();

    try {
      await processPaymentInStripe(checkoutResponseBody);
    } catch (error) {
      if (error instanceof CheckoutError) {
        throw new PostAttemptCheckoutError(error);
      }

      throw error;
    }
  };

  const onSubmitMutation = useMutation<any, any, any>({
    mutationFn: async data => {
      const formData = data;
      const { stripeElements: elements, stripe } =
        getStripeElements("onSubmitMutation");

      idempotencyKeyRef.current ??= generateIdempotencyKey();

      try {
        if (isCardInfoRequired) {
          // Digital wallets (e.g. Apple and Google Pay) seem to have an acceptable time window between the
          // time the user clicks the pay button and the time the Payment Element is submitted. If we do not
          // do this as soon as possible, Apple/Google Pay will fail to load with an error message that says:
          // > Something went wrong. Unable to show Google Pay. Please choose a different payment method and try again.
          //
          // See PR for more context: https://github.com/circleco/circle/pull/22389
          await submitStripeElement(
            "useCard.onSubmitMutation.isCardInfoRequired",
          );

          if (isSettingUpPaymentMethod) {
            const prepareResponse = await paywallCheckoutApi.prepare(
              currentCommunity.id,
              selectedPrice.id,
              checkoutIntentToken,
            );
            if (!prepareResponse || !prepareResponse.ok) {
              const error = await prepareResponse?.json?.();
              throw CheckoutError.fromPaywallError(
                "useCard.onSubmitMutation.prepare",
                error,
              );
            }

            const prepareResponseBody = await prepareResponse.json();
            const setupIntentSecret =
              prepareResponseBody.setup_intent.client_secret;

            let setupIntentResult =
              await stripe.retrieveSetupIntent(setupIntentSecret);
            if (setupIntentResult.error) {
              throw CheckoutError.fromStripeError(
                "useCard.onSubmitMutation.retrieveSetupIntent",
                setupIntentResult.error,
              );
            }

            if (setupIntentResult.setupIntent.status !== "succeeded") {
              setupIntentResult = await stripe.confirmSetup({
                elements,
                clientSecret: setupIntentSecret,
                confirmParams: {
                  return_url: window.location.href,
                  ...paymentMethodDataParams,
                },
                redirect: "if_required",
              });
              if (setupIntentResult.error) {
                throw CheckoutError.fromStripeError(
                  "useCard.onSubmitMutation.confirmSetup",
                  setupIntentResult.error,
                );
              }

              formData.payment_method_processor_id =
                setupIntentResult.setupIntent.payment_method; // Pass payment method ID for checkout fulfillment.
            }
          }
        }

        await execCheckoutRequest(formData);
        idempotencyKeyRef.current = undefined;
      } catch (error) {
        if (error instanceof PostAttemptCheckoutError) {
          throw error.checkoutError;
        }

        if (error instanceof CheckoutError) {
          idempotencyKeyRef.current = undefined;
        }

        throw error;
      }
    },
  });
  return { onSubmitMutation };
};
